import { useEffect, useRef, useState } from "react";
import { MIN_PIN_DIGITS, isValidPin, keypairFromSecret, phraseProblem, publicKeyToNpub, secretKeyFromPhrase, type PhraseProblem } from "@timecapsule/crypto";
import { api } from "../api";
import { protectKey, prove, registerKey } from "../keys";
import { PasskeyError, createPasskey, isAborted } from "../passkey";
import { isKeyRequestCancelled, useSession } from "../session";
import { errorText, useT, type Messages } from "../i18n";

/** PIN and its repetition; reports the PIN only when it is valid and both fields match. */
export function PinFields({ onChange, disabled }: { onChange: (pin: string | null) => void; disabled?: boolean }) {
  const t = useT();
  const [first, setFirst] = useState("");
  const [second, setSecond] = useState("");
  const bad = first.length > 0 && !isValidPin(first);
  const mismatch = second.length > 0 && first !== second;
  const valid = isValidPin(first) && first === second;

  useEffect(() => onChange(valid ? first : null), [valid, first, onChange]);

  return (
    <div className="fields">
      <label>
        {t.common.pin}
        <input type="password" inputMode="numeric" autoComplete="new-password" value={first} disabled={disabled} onChange={(e) => setFirst(e.target.value.trim())} />
      </label>
      <label>
        {t.common.repeatPin}
        <input type="password" inputMode="numeric" autoComplete="new-password" value={second} disabled={disabled} onChange={(e) => setSecond(e.target.value.trim())} />
      </label>
      {bad && <p className="error small">{t.keySetup.pinRule(MIN_PIN_DIGITS)}</p>}
      {mismatch && <p className="error small">{t.keySetup.pinsDiffer}</p>}
    </div>
  );
}

function describeProblem(t: Messages, p: PhraseProblem): string {
  switch (p.code) {
    case "WORD_COUNT":
      return t.keySetup.wordCount(p.count);
    case "UNKNOWN_WORD":
      return t.keySetup.unknownWord(p.index + 1, p.word);
    case "CHECKSUM":
      return t.keySetup.checksum;
  }
}

/** Twelve typed words -> secret key. Reports the key only for a valid phrase. */
export function PhraseInput({ onKey, disabled }: { onKey: (secretKey: Uint8Array | null) => void; disabled?: boolean }) {
  const t = useT();
  const [text, setText] = useState("");
  const problem = text.trim() ? phraseProblem(text) : null;

  useEffect(() => onKey(text.trim() && !problem ? secretKeyFromPhrase(text) : null), [text, problem, onKey]);

  return (
    <div className="fields">
      <label>
        {t.common.recoveryPhrase}
        <textarea rows={3} autoComplete="off" autoCapitalize="none" spellCheck={false} value={text} disabled={disabled} onChange={(e) => setText(e.target.value)} />
      </label>
      {problem && <p className={problem.code === "WORD_COUNT" ? "muted small" : "error small"}>{describeProblem(t, problem)}</p>}
    </div>
  );
}

type Passkey = { credentialId: string; prf: Uint8Array; publicKey: string | null; alg: number | null };

/**
 * Lets the new passkey also sign in (PLAN.md 4.14), proven by the key it protects. Optional: a
 * browser that does not report the public key, or a refusal, leaves the setup complete and the
 * member signs in another way.
 */
async function enrolForSignIn(secretKey: Uint8Array, pk: Passkey): Promise<void> {
  if (!pk.publicKey || pk.alg === null) return;
  try {
    const body = { credential_id: pk.credentialId, alg: pk.alg, public_key: pk.publicKey };
    await api.enrolPasskey(body, await prove(secretKey, "passkey", [pk.credentialId, String(pk.alg), pk.publicKey]));
  } catch (err) {
    console.warn("passkey sign-in not enrolled", err);
  }
}

/**
 * Last step of every setup: PIN, passkey, device copy, server copy, registration.
 * `serverCopy` false keeps the existing server copy (device restored from it).
 * Switching an account to another identity needs the current key on this device (PLAN.md 4.11 H1).
 */
export function ProtectKey({ secretKey, serverCopy, onDone }: { secretKey: Uint8Array; serverCopy: boolean; onDone: () => void }) {
  const t = useT();
  const { me, adoptKey, keyStatus, withKey, confirmIdentity } = useSession();
  const [pin, setPin] = useState<string | null>(null);
  const [passkey, setPasskey] = useState<Passkey | null>(null);
  const [noPrf, setNoPrf] = useState<string | null>(null);
  const [busy, setBusy] = useState<"saving" | "passkey" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pubkey = keypairFromSecret(secretKey).publicKey;
  const switching = me.pubkey !== null && me.pubkey !== pubkey;
  const [switchUnderstood, setSwitchUnderstood] = useState(false);
  const blocked = switching && !switchUnderstood;
  const abort = useRef<AbortController | null>(null);

  async function finish(withPasskey: Passkey | null) {
    setBusy("saving");
    // Register first: the server accepts a server copy only for the active identity. A new key
    // always asks the member to sign in again first (PLAN.md 4.16 K3).
    if (me.pubkey !== pubkey) {
      // The account's very first key: a fresh Google sign-in is enough (PLAN.md 4.18 step 5).
      await confirmIdentity(t.reauth.purposes.createKey, { reuseRecent: true, strong: me.had_key });
      if (!switching) await registerKey(secretKey, null);
      else {
        if (keyStatus === "none" || keyStatus === "mismatch") throw new Error(t.keySetup.switchNeedsKey);
        await withKey(t.keySetup.switchPurpose, (currentKey) => registerKey(secretKey, currentKey));
      }
    }
    const record = await protectKey({ secretKey, pin, passkey: withPasskey, serverCopy });
    if (withPasskey) await enrolForSignIn(secretKey, withPasskey);
    await adoptKey(record, secretKey);
    onDone();
  }

  async function withPasskey() {
    setError(null);
    try {
      let pk = passkey;
      if (!pk) {
        setBusy("passkey");
        abort.current = new AbortController();
        const created = await createPasskey({ name: me.email, displayName: t.keySetup.passkeyName(me.display_name) }, abort.current.signal);
        abort.current = null;
        if (!created.prf) {
          setNoPrf(t.keySetup.noPrf);
          setBusy(null);
          return;
        }
        pk = { credentialId: created.credentialId, prf: created.prf, publicKey: created.publicKey, alg: created.alg };
        setPasskey(pk);
      }
      await finish(pk);
    } catch (err) {
      abort.current = null;
      if (err instanceof PasskeyError && (err.code === "UNSUPPORTED" || err.code === "NO_PRF")) setNoPrf(err.message);
      else if (!isAborted(err) && !isKeyRequestCancelled(err)) setError(errorText(err));
      setBusy(null);
    }
  }

  async function withoutPasskey() {
    setError(null);
    try {
      await finish(null);
    } catch (err) {
      if (!isKeyRequestCancelled(err)) setError(errorText(err));
      setBusy(null);
    }
  }

  return (
    <>
      <h2>{t.keySetup.title}</h2>
      {switching && (
        <div className="warn">
          <p>{t.keySetup.switchText(publicKeyToNpub(pubkey), publicKeyToNpub(me.pubkey!))}</p>
          <p>{t.keySetup.switchPrivate}</p>
          <label className="radio">
            <input type="checkbox" checked={switchUnderstood} onChange={(e) => setSwitchUnderstood(e.target.checked)} /> {t.keySetup.understand}
          </label>
        </div>
      )}
      {!noPrf ? (
        <>
          <p>{t.keySetup.choose(serverCopy)}</p>
          <p className="small muted">{t.keySetup.pinProtects(serverCopy)}</p>
          <PinFields onChange={setPin} disabled={!!busy} />
          <button className="primary" disabled={!pin || !!busy || blocked} onClick={withPasskey}>
            {passkey ? t.common.finish : t.keySetup.createPasskey}
          </button>
          {busy === "passkey" && (
            <button type="button" onClick={() => abort.current?.abort()}>
              {t.common.cancel}
            </button>
          )}
        </>
      ) : (
        <div className="card">
          <p className="warn">{noPrf}</p>
          <p className="small">{t.keySetup.withoutPasskeyText}</p>
          <button disabled={!!busy || blocked} onClick={withoutPasskey}>
            {t.keySetup.withoutPasskey}
          </button>
        </div>
      )}
      {busy && <p className="muted">{busy === "passkey" ? t.common.waitingPasskey : t.keySetup.saving}</p>}
      {busy === "passkey" && <p className="small muted">{t.common.noWindowHint}</p>}
      {error && <p className="error">{error}</p>}
    </>
  );
}
